import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Content,
  Text,
  Accordion,
  Modal,
  Button,
  Stack,
  Form,
  Grid,
  Row,
  Col,
  FlexboxGrid,
  Divider,
} from 'rsuite';
import PortalHeader from '../../components/layout/PortalHeader';
import {
  selectCalculatedTaxData,
  selectTaxFlowData,
} from '../../store/slices/tax/taxSelectors';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import TaxPayableBreakdownTable from '../../components/tax/BreakdownTable/TaxPayableBreakdownTable';
import dayjs from 'dayjs';
import { resetCalculateTax } from '../../store/slices/tax/taxSlice';
import { deriveConfigExpressTaxCertifications } from '../../store/slices/config/configSelectors';
import AnnotatedCheckbox from '../../components/form/AnnotatedCheckbox';
import {
  constructMarkup,
  convertNumberToCurrency,
  getAddressWithUnitNumber,
  isNullUndefinedOrEmptyString,
  titleize,
} from '../../common/utils';
import FormTextField from '../../components/form/FormTextField';
import { checkedValidation, requiredValidation } from '../../common/validation';
import { BREAKPOINT_ENUM, FLOWS_ENUM } from '../../common/enums';
import ResponsiveModal from '../../components/ResponsiveModal';
import { setSignatoryName } from '../../store/slices/payment/paymentSlice';
import { payNothingTax } from '../../store/slices/payment/paymentThunk';
import { selectCurrentUserEmail } from '../../store/slices/auth/authSelectors';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import Notice from '../../components/user-feedback/Notice';
import IconIndicator from '../../components/user-feedback/IconIndicator';

const TaxConfirmAndSign = () => {
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();
  const form = useRef();

  const formData = useSelector(selectTaxFlowData);
  const certifications = useSelector(deriveConfigExpressTaxCertifications);
  const calculatedTaxData = useSelector(selectCalculatedTaxData);
  const selectedEmailAddress = useSelector(selectCurrentUserEmail);

  const [hasError, setHasError] = useState(false);
  const [processingPayNothing, setProcessingPayNothing] = useState(false);
  const [taxSummaryModalOpen, setTaxSummaryModalOpen] = useState(false);

  // all curently expanded period-license combinations
  const [activeKeys, setActiveKeys] = useState([]);

  // most recently clicked on license. Used to populate the modal on small screens
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= BREAKPOINT_ENUM.SMALL
  );

  const [formError, setFormError] = useState({});
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    // Reset the calculate tax properties in redux
    // We need to do this so that if we navigate back to the previous page we are not immidiatley sent back to this page
    dispatch(resetCalculateTax());

    // On small screens the accordion detail should open in a modal
    const handleResize = () => {
      const isMobileSize = window.innerWidth <= BREAKPOINT_ENUM.SMALL;
      setIsMobile(isMobileSize);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile && selectedLicense && selectedPeriod) {
      setTaxSummaryModalOpen(true);
    } else if (!isMobile) {
      setTaxSummaryModalOpen(false);
    }
  }, [isMobile, selectedLicense, selectedPeriod]);

  const selectedPeriods = useMemo(() => {
    return formData.selectedLicenses || []; //todo if there are no licenses what should happen on this page
  }, [formData]);

  const handleSelectLicense = (eventKey, period) => {
    // event key is startDate_licenseNumber
    const licenseNumber =
      eventKey && eventKey.includes('_') ? eventKey.split('_')[1] : undefined;

    // closing
    if (activeKeys.includes(eventKey)) {
      clearSelectedLicense();
    } else {
      // opening
      const newActiveKey = [...activeKeys, eventKey];
      const selectedLicense = period.licenses.find(
        (license) => license.licenseNumber === licenseNumber
      );
      setSelectedPeriod(period);
      setSelectedLicense(selectedLicense);
      setActiveKeys(newActiveKey);
    }
  };

  const clearSelectedLicense = useCallback(() => {
    setTaxSummaryModalOpen(false);
    setSelectedLicense(null);
    setSelectedPeriod(null);
    const newActiveKeys = activeKeys.filter(
      (item) =>
        item !==
        `${selectedPeriod?.startDate}_${selectedLicense?.licenseNumber}`
    );
    setActiveKeys(newActiveKeys);
  }, [selectedLicense, selectedPeriod]);

  const formatDate = (dateString) => {
    const date = dayjs(dateString);
    return date.format('YYYY-MM-DD');
  };

  const datesAreTheSame = (dateOne, dateTwo) => {
    const dayOneDayJs = dayjs(dateOne);
    const dayTwoDayJs = dayjs(dateTwo);
    return dayOneDayJs.isSame(dayTwoDayJs);
  };

  const formModel = useMemo(() => {
    let certificationMap = {};
    if (certifications) {
      certifications?.forEach(
        (c, idx) => (certificationMap[`certification_${idx}`] = false)
      );
    }

    return {
      ...certificationMap,
      signatory: '',
      date: dayjs().format('YYYY-MM-DD'),
    };
  }, [certifications]);

  const validationModel = useMemo(() => {
    let certificationValidationMap = {};
    if (certifications) {
      certifications?.forEach(
        (c, idx) =>
          (certificationValidationMap[`certification_${idx}`] =
            checkedValidation)
      );
    }
    return {
      signatory: requiredValidation,
      ...certificationValidationMap,
    };
  }, [certifications]);

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  const getAddress = useMemo(() => {
    return (licenseNumber, startDate, endDate) => {
      const selectedLicensesToPayTaxOn = selectedPeriods;
      const matchingPeriod = selectedLicensesToPayTaxOn.find(
        (period) =>
          datesAreTheSame(period.startDate, startDate) &&
          datesAreTheSame(period.endDate, endDate)
      );

      const matchingLicense = matchingPeriod?.licenses.find(
        (property) => property.license === licenseNumber
      );
      const address = matchingLicense?.address;

      if (!isNullUndefinedOrEmptyString(address)) {
        return getAddressWithUnitNumber(address, matchingLicense?.unitNumber);
      } else {
        return 'Unkown';
      }
    };
  }, [selectedPeriods]);

  const findOrCreateGroupForPeriod = (groups, item) => {
    const formattedStartDate = formatDate(item.startDate);
    const formattedEndDate = formatDate(item.endDate);

    let group = groups?.find(
      (group) =>
        datesAreTheSame(group.startDate, item.startDate) &&
        datesAreTheSame(group.endDate, item.endDate)
    );
    if (!group) {
      group = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        label: item.period,
        licenses: [],
        totalDueForPeriod: 0,
      };
      groups.push(group);
    }
    return group;
  };

  const convertedDataGroupedByPeriod = useMemo(() => {
    if (!calculatedTaxData?.details) return [];
    const sortedData = [...calculatedTaxData?.details]?.sort(
      (a, b) => new Date(b.endDate) - new Date(a.endDate)
    );

    // Group data by start and end dates
    const displayTaxablePeriods = sortedData?.reduce(
      (allTaxablePeriods, calculatedLicenseTaxData) => {
        let taxablePeriod = findOrCreateGroupForPeriod(
          allTaxablePeriods,
          calculatedLicenseTaxData
        );

        let license = {
          licenseNumber: calculatedLicenseTaxData.licence_no,
          address: getAddress(
            calculatedLicenseTaxData.licence_no,
            taxablePeriod.startDate,
            taxablePeriod.endDate
          ),
          providers: [],
          totalDueForPeriod: 0,
        };

        // Iterate over each taxable activity for the license
        calculatedLicenseTaxData.misc?.taxableActivities.forEach((activity) => {
          let provider = {
            key: activity.provider || 'All',
            sales: activity.totalRevenue,
            deductions: activity.allowableDeductions,
            taxableReceipts: activity.taxableReceipts,
            taxTypes: activity.taxTypes,
          };

          const totalDueForLicenseForPeriodForProvider = Object.values(
            activity.taxTypes
          ).reduce((sum, tax) => sum + tax.total, 0);
          license.totalDueForPeriod += totalDueForLicenseForPeriodForProvider;
          taxablePeriod.totalDueForPeriod +=
            totalDueForLicenseForPeriodForProvider;

          license.providers.push(provider);
        });
        taxablePeriod.licenses.push(license);
        return allTaxablePeriods; // Ensure to return the accumulator of reduce function
      },
      []
    );

    return displayTaxablePeriods; // Return the final grouped and processed data
  }, [calculatedTaxData]);

  // calculate total tax payable for the selected properties
  const totalPaymentDue = useMemo(() => {
    let totalDue = convertedDataGroupedByPeriod.reduce(
      (total, reportingPeriod) => total + reportingPeriod.totalDueForPeriod,
      0
    );
    return convertNumberToCurrency(totalDue);
  }, [convertedDataGroupedByPeriod]);

  const paymentRequired = useMemo(() => {
    const totalPaymentDue = calculatedTaxData?.summary?.total;
    return totalPaymentDue != null && totalPaymentDue > 0;
  }, [calculatedTaxData]);

  const handleFinaliseOrNextClick = () => {
    // Validate the form
    const valid = form.current.check();

    if (valid) {
      dispatch(setSignatoryName(formValues.signatory));
      if (paymentRequired) {
        return true;
      } else {
        setProcessingPayNothing(true);
        dispatch(payNothingTax(selectedEmailAddress, onPayNothingCompleted));
        // don't return anything from here, its handled by the onPayNothingCompleted function
      }
    }
    return false;
  };

  const onPayNothingCompleted = (successfull) => {
    setHasError(successfull);
    setProcessingPayNothing(false);
    if (successfull) {
      placeNavigate('/tax-success');
    }
  };

  const panelExpanded = useCallback(
    (key) => {
      return activeKeys?.includes(key);
    },
    [activeKeys]
  );

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader title="Pay Tax" />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            flow={FLOWS_ENUM.PAY_TOT}
            title="Confirm and Sign"
            onNext={handleFinaliseOrNextClick}
          >
            <Text className="text-large mb-24">
              Please confirm the following information and sign below
            </Text>
            <Text className="header-text-h4 text-bold mb-24">Tax Summary</Text>
            <Divider className="bg-light-grey" />
            <div>
              {convertedDataGroupedByPeriod?.map((period) => {
                return (
                  <div
                    key={`period_${period.startDate}_${period.endDate}`}
                    style={{ marginBottom: '30px' }}
                  >
                    <Stack
                      justifyContent="space-between"
                      className="mb-16"
                    >
                      <Stack.Item>
                        <Text
                          className="text-bold"
                          style={{ fontSize: '14px' }}
                        >
                          {period.label}
                        </Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Text
                          className="text-small"
                          style={{ paddingRight: '50px' }}
                        >
                          Tax due
                        </Text>
                      </Stack.Item>
                    </Stack>
                    <Accordion
                      bordered
                      onSelect={(eventKey) => {
                        handleSelectLicense(eventKey, period);
                      }}
                      className="no-border-accordion"
                    >
                      {period.licenses?.map((license) => {
                        return (
                          <Accordion.Panel
                            key={`period_license_${license.licenseNumber}`}
                            className="tax-accordion-header"
                            eventKey={`${period.startDate}_${license.licenseNumber}`}
                            header={
                              <Stack justifyContent="space-between">
                                <Stack.Item>
                                  <Text
                                    className="text-medium"
                                    style={{ fontWeight: 400 }}
                                  >
                                    {titleize(license.address)}
                                  </Text>
                                </Stack.Item>
                                <Stack.Item>
                                  <Text
                                    className="text-medium pr-16"
                                    style={{ fontWeight: 400 }}
                                  >
                                    {convertNumberToCurrency(
                                      license.totalDueForPeriod
                                    )}
                                  </Text>
                                </Stack.Item>
                              </Stack>
                            }
                            expanded={panelExpanded(
                              `${period.startDate}_${license.licenseNumber}`
                            )}
                          >
                            <div className="tax-accordion-content">
                              <TaxPayableBreakdownTable
                                period={period}
                                license={license.providers}
                                periodLabel={period.label}
                              />
                            </div>
                          </Accordion.Panel>
                        );
                      })}
                    </Accordion>
                    <Stack
                      justifyContent="space-between"
                      className="pt-16"
                    >
                      <Text
                        className="text-bold"
                        style={{ fontSize: '14px' }}
                      >
                        Total due {period.label}
                      </Text>
                      <Text
                        className="text-bold"
                        style={{ fontSize: '16px', marginRight: '45px' }}
                      >
                        {convertNumberToCurrency(period.totalDueForPeriod)}
                      </Text>
                    </Stack>
                    <Divider className="bg-light-grey" />
                  </div>
                );
              })}
            </div>
            <Stack justifyContent="space-between">
              <Text
                className="text-bold"
                style={{ fontSize: '16px' }}
              >
                Total Payment Due
              </Text>
              <Text
                className="text-bold"
                style={{ fontSize: '16px', marginRight: '45px' }}
              >
                {totalPaymentDue}
              </Text>
            </Stack>
            <Divider className="bg-bright-zenith" />
            <Form
              ref={form}
              fluid
              checkTrigger="none"
              onCheck={(errors) => {
                setFormError(errors);
              }}
              formDefaultValue={formModel}
              onChange={(e) => {
                setFormValues(e);
              }}
              className="mt-24 pt-24"
            >
              <Text className="header-text-h4 text-bold mb-12">
                Certifications
              </Text>
              {certifications?.map((certificaton, idx) => {
                return (
                  <AnnotatedCheckbox
                    style={{ marginTop: 0, marginBottom: '16px' }}
                    key={`certification_${idx}`}
                    label={constructMarkup(certificaton)}
                    hasError={formError[`certification_${idx}`]}
                    name={`certification_${idx}`}
                    validationRule={validationModel[`certification_${idx}`]}
                  />
                );
              })}
              <Grid
                fluid
                className="pt-24"
              >
                <FlexboxGrid
                  as={Row}
                  align="bottom"
                >
                  <Col
                    xs={24}
                    md={10}
                    className="mb-16"
                  >
                    <FormTextField
                      label="Legal Name of Authorized Signatory"
                      name="signatory"
                      hasError={formError['signatory']}
                      validationRule={validationModel.signatory}
                      errorMessage="Signatory name is required"
                    ></FormTextField>
                  </Col>
                  <Col
                    xs={24}
                    md={5}
                    className="mb-16"
                  >
                    <Text style={{ marginBottom: '4px' }}>Date</Text>
                    <Text className="report-revenue-calculated-value">
                      {formValues?.date}
                    </Text>
                  </Col>
                </FlexboxGrid>
                {hasError && (
                  <Row>
                    <Col xs={24}>
                      <Notice message="Something went wrong while processing your request." />
                    </Col>
                  </Row>
                )}
                {processingPayNothing && (
                  <Row>
                    <Col xs={24}>
                      <IconIndicator />
                    </Col>
                  </Row>
                )}
              </Grid>
            </Form>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>

      <ResponsiveModal
        isOpen={taxSummaryModalOpen}
        onClose={() => {
          clearSelectedLicense();
        }}
        ariaLabel="tax-confirm-and-sign"
      >
        {selectedLicense && selectedPeriod && (
          <>
            <Modal.Body style={{ marginTop: '10px', paddingLeft: '4px' }}>
              <Grid fluid>
                <Row>
                  <Col xs={24}>
                    <Text className="text-bold mb-16">
                      {selectedPeriod?.label}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24}>
                    <Text className="text-bold mb-16">
                      {selectedLicense?.address}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col xs={24}>
                    <TaxPayableBreakdownTable
                      period={selectedPeriod}
                      license={selectedLicense?.providers}
                      periodLabel={selectedPeriod?.Label}
                      totalPayment={totalPaymentDue}
                    />
                  </Col>
                </Row>
              </Grid>
            </Modal.Body>
            <Modal.Footer style={{ padding: '10px' }}>
              <Button
                onClick={() => {
                  clearSelectedLicense();
                }}
                className="btn-primary"
                style={{ width: '100%' }}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </ResponsiveModal>
    </FlexboxGrid>
  );
};

export default TaxConfirmAndSign;
